import FormInput from '@form/FormInput';
import moment from 'moment';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useClubSlug } from '../../Clubs/hooks';
import { estimateOccurrences, tieredPriceRange } from '../PriceTiers/calculateTieredPrice';
import { SubscriptionTemplate } from '../types';

const SubscriptionTemplateInput: FC<React.PropsWithChildren<React.PropsWithChildren<unknown>>> = () => {
    const { t } = useTranslation('space-manager');
    const clubSlug = useClubSlug();
    const { data: subscriptionTemplateData, isLoading: subscriptionTemplateIsLoading } = useSWR<{
        data: SubscriptionTemplate[];
    }>(`club/${clubSlug}/space-manager/subscription-template`);
    const { watch, setValue, getValues } = useFormContext();
    const previousSubscriptionTemplateId = useRef<number>(undefined);
    const subscriptionTemplateId = watch('subscription_template_id');
    // the staggering preview has to follow the times the admin is editing right now
    const [start, end, repeatWeekdays, repeatPeriod, repeatUntil, exceptions] = watch([
        'start',
        'end',
        'repeat_weekdays',
        'repeat_period',
        'repeat_until',
        'exceptions'
    ]);
    const subscriptionTemplate = subscriptionTemplateData?.data?.find((st) => st.id === Number(subscriptionTemplateId));

    useEffect(() => {
        if (previousSubscriptionTemplateId.current === subscriptionTemplateId) return;
        // the list arrives async, acting before it is here would write an invalid date into every field
        if (subscriptionTemplateId && !subscriptionTemplate) return;

        previousSubscriptionTemplateId.current = subscriptionTemplateId;

        if (!subscriptionTemplate) return;

        // the end input only carries a time of day, so its date has to follow the day of the start.
        // Moving it to the template start instead let the series run backwards and the server answered
        // with "Enddatum muss nach dem Start sein" until the end time was typed in again.
        const startMoment = moment(getValues('start'));
        const endMoment = moment(getValues('end'));

        if (startMoment.isValid() && endMoment.isValid()) {
            const alignedEnd = startMoment
                .clone()
                .set({ hour: endMoment.hour(), minute: endMoment.minute(), second: 0, millisecond: 0 });

            if (!alignedEnd.isAfter(startMoment)) alignedEnd.add(1, 'day');

            setValue('end', alignedEnd.toDate());
        }

        setValue('repeat_period', subscriptionTemplate.repeat_period_in_days);
        setValue('repeat_until', moment(subscriptionTemplate.end).toDate());
        setValue('exceptions', subscriptionTemplate.exceptions ?? []);
        setValue('title', subscriptionTemplate.allocation_title ?? 'Platzabo');
        setValue('allocation_group_id', subscriptionTemplate.allocation_group_id ?? null);
    }, [subscriptionTemplateId, subscriptionTemplate]);

    const basisLabel = useMemo(
        () => ({
            total: t('allocation.subscriptionTemplate.flat'),
            per_allocation: t('allocation.subscriptionTemplate.perAllocation'),
            per_hour: t('allocation.subscriptionTemplate.perHour')
        }),
        [t]
    );

    /**
     * Unbinding preview: the price actually stored is the one the server calculates on save.
     * See components/SpaceManager/PriceTiers/calculateTieredPrice.ts.
     */
    const priceString = useMemo(() => {
        if (!subscriptionTemplate) return null;

        const basis = subscriptionTemplate.price_basis ?? 'total';

        if (subscriptionTemplate.pricing_mode !== 'tiered') {
            return `${subscriptionTemplate.price} € ${basisLabel[basis]}`;
        }

        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;
        if (!startDate || !endDate) return null;

        const divisor =
            basis === 'total'
                ? estimateOccurrences(
                      startDate,
                      repeatUntil ? new Date(repeatUntil) : startDate,
                      repeatPeriod,
                      repeatWeekdays,
                      exceptions
                  )
                : 1;

        const totals = tieredPriceRange(
            subscriptionTemplate.price_tiers ?? [],
            startDate,
            endDate,
            repeatWeekdays,
            basis
        ).map((total) => total / divisor);

        if (!totals.length) return null;

        const format = (value: number) => value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const range = totals.length > 1 ? `${format(totals[0])} – ${format(totals[totals.length - 1])}` : format(totals[0]);

        return `${range} € ${t('allocation.subscriptionTemplate.perAllocation')}`;
    }, [subscriptionTemplate, start, end, repeatWeekdays, repeatPeriod, repeatUntil, exceptions, basisLabel, t]);

    const descriptionString = useMemo(() => {
        if (!subscriptionTemplate) return null;

        return [
            `${moment(subscriptionTemplate.start).format('DD.MM.YYYY')} - ${moment(subscriptionTemplate.end).format(
                'DD.MM.YYYY'
            )}`,
            t('allocation.subscriptionTemplate.repeatEvery', { days: subscriptionTemplate.repeat_period_in_days }),
            priceString
        ]
            .filter(Boolean)
            .join(' | ');
    }, [subscriptionTemplate, priceString, t]);

    return (
        <div>
            <FormInput.Select
                name="subscription_template_id"
                label={t('allocation.subscriptionTemplate.label')}
                options={subscriptionTemplateData?.data?.map((st) => ({ key: st.id, label: st.name })) ?? []}
            />
            <span className="tw-block tw-text-center tw-text-sm tw-italic tw-text-accents-7">{descriptionString}</span>
        </div>
    );
};

export default SubscriptionTemplateInput;
